import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { List } from 'immutable';
import { TreeNode } from 'primeng/api';
import { Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';

import { BaseComponent } from '../../../../components/base/base.component';
import { IColumnModel } from '../../../../models/doview/column.model';
import { INodeInstanceViewModel } from '../../../../models/node/node-instance.view.model';
import { IProjectModel } from '../../../../models/project/project.model';
import { ITestRunRowModel, ITestRunCellEditEvent } from '../../../../models/test-runs/test-run-row.model';
import { IWorkspaceInstanceModel } from '../../../../models/workspace/workspace-instance.model';
import { FIELD_NAME_NAME_LOWER, LABEL_RUN_KIND, RELATED_BUG_KIND } from '../../../../modules/catch-core/data/constants/blueprint.constants';

import { DashboardHelper } from '../../../../services/dashboard.service';
import { NodeHelper } from '../../../../services/helpers/node.helper';
import { NodeTableHelper } from '../../../../services/helpers/node-table.helper';
import { TestRunsStore } from '../../../../services/stores/test-runs.store';
import { WorkspaceStore } from '../../../../services/stores/workspace.store';
import {
  ICellEditEvent,
  ISortChangeEvent,
} from '../../../../components/node-summary-table/cell-edit-event';
import {
  buildCollapsedRunsColumns,
  buildExpandedRunsColumns,
  hydrateColumnFieldIds,
} from './runs-column-definitions';

@Component({
  selector: 'app-edit-panel-runs-pane',
  templateUrl: './edit-panel-runs-pane.component.html',
  styleUrls: ['./edit-panel-runs-pane.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EditPanelRunsPaneComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {
  @Input() selectedNode: INodeInstanceViewModel | null = null;
  @Input() selectedProject: IProjectModel | null = null;
  @Input() selectedWorkspace: IWorkspaceInstanceModel | null = null;
  @Input() isExpanded: boolean = false;

  @Output() runsCountChange = new EventEmitter<number>();
  @Output() expandedChange = new EventEmitter<boolean>();

  public rows: TreeNode[] = [];
  public rowsList: List<ITestRunRowModel> = List<ITestRunRowModel>([]);
  public chartRows: ITestRunRowModel[] = [];
  public frozenColumns: IColumnModel[] = [];
  public scrollableColumns: IColumnModel[] = [];
  public tableMinWidth: string = 'auto';
  public sortField: string = '';
  public sortOrder: number = -1;
  public isLoading: boolean = false;
  public lastError: string | null = null;

  private storeSub?: Subscription;
  private loadingSub?: Subscription;
  private errorSub?: Subscription;
  private modifiedSub?: Subscription;
  // Angular calls ngOnChanges before ngOnInit for any bound @Input on first change detection, so
  // ngOnChanges already triggers the initial reloadIfReady() for a template-bound selectedNode.
  // Without this guard, ngOnInit's own reloadIfReady() call fires GetTestRuns a second time.
  private reloadedViaOnChanges = false;

  constructor(
    private readonly testRunsStore: TestRunsStore,
    private readonly workspaceStore: WorkspaceStore,
    private readonly nodeHelper: NodeHelper,
    private readonly nodeTableHelper: NodeTableHelper,
    private readonly dashboardHelper: DashboardHelper,
    private readonly cdr: ChangeDetectorRef,
  ) {
    super();
  }

  public ngOnInit(): void {
    this.rebuildColumns();
    this.storeSub = this.testRunsStore.$runs.subscribe((runs) => {
      this.rowsList = runs;
      this.chartRows = runs?.toArray().filter((r) => r?.rowKind === LABEL_RUN_KIND) ?? [];
      this.rebuildColumns();
      this.rows = this.buildTreeNodes(runs);
      this.runsCountChange.emit(runs?.toArray().filter((r) => r?.rowKind === LABEL_RUN_KIND).length ?? 0);
      this.cdr.markForCheck();
    });
    this.loadingSub = this.testRunsStore.$isLoading.subscribe((v) => {
      this.isLoading = v;
      this.cdr.markForCheck();
    });
    this.errorSub = this.testRunsStore.$lastError.subscribe((v) => {
      this.lastError = v;
      this.cdr.markForCheck();
    });
    // Refetch whenever the loaded node is patched anywhere in the panel
    // (Steps tab, Overview status dropdown, header inputs, etc.). Container's
    // saveWork fires setModifiedPropertyItem after each successful
    // $patchWSINode, so this catches every save-driven update without racing
    // the in-flight HTTP. Self-edits from this pane go through patchRun
    // which does not fire setModifiedPropertyItem, so we don't loop.
    // skip(1): modifiedPropertyItem is a BehaviorSubject and replays the last save on subscribe.
    // Every $patchWSINode (inline edit, board drag, node creation...) sets it, so opening the panel
    // on a node saved earlier would otherwise fire GetTestRuns a second time on mount.
    this.modifiedSub = this.dashboardHelper.modifiedPropertyItem.pipe(skip(1)).subscribe((modified) => {
      if (!modified?.id) return;
      if (modified.id !== this.testRunsStore.currentNodeId) return;
      this.reloadIfReady();
    });
    // Only needed when selectedNode isn't template-bound (so ngOnChanges never ran) — e.g. a
    // parent that sets the input imperatively. See reloadedViaOnChanges above.
    if (!this.reloadedViaOnChanges) {
      this.reloadIfReady();
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedNode']) {
      // Only reload runs when a *different* node is selected — skip when the
      // same node is re-emitted after a field-level update (e.g. runs-tab
      // inline edit syncing back to WorkspaceStore). The runs table already
      // reflects the edit via TestRunsStore's optimistic update.
      const prev = changes['selectedNode'].previousValue;
      const curr = changes['selectedNode'].currentValue;
      if (!prev || prev?.id !== curr?.id) {
        this.reloadedViaOnChanges = true;
        this.reloadIfReady();
      }
    }
    if (changes['selectedWorkspace'] && !changes['selectedWorkspace'].firstChange) {
      this.rebuildColumns();
    }
    // Fully controlled by the parent's isExpanded Input (driven by
    // dashboardHelper.isRightPanelExpanded). Rebuild columns + reset row tree
    // when the panel toggles so bug-children collapse back in compact view.
    if (changes['isExpanded']) {
      this.rebuildColumns();
      // Drop prior expansion before rebuilding so buildTreeNodes can't
      // re-apply it from the snapshot — compact mode hides the toggler, so
      // any leftover `expanded: true` would strand bug children visible.
      this.rows = [];
      this.rows = this.buildTreeNodes(this.rowsList);
      this.cdr.markForCheck();
    }
  }

  public toggleTableExpanded(): void {
    this.expandedChange.emit(!this.isExpanded);
  }

  public onCollapsedAreaClick(): void {
    if (!this.isExpanded) {
      this.expandedChange.emit(true);
    }
  }

  public override ngOnDestroy(): void {
    this.storeSub?.unsubscribe();
    this.loadingSub?.unsubscribe();
    this.errorSub?.unsubscribe();
    this.modifiedSub?.unsubscribe();
    super.ngOnDestroy();
  }

  private reloadIfReady(): void {
    const nodeId = this.selectedNode?.id;
    const projectId = this.selectedProject?.id ?? '';
    const workspaceId = this.selectedWorkspace?.id ?? null;
    if (!nodeId) {
      this.testRunsStore.clear();
      return;
    }
    this.testRunsStore.loadRuns(nodeId, projectId, workspaceId);
  }

  private rebuildColumns(): void {
    const fieldIdDictionary = this.nodeHelper.getFieldNameToIdDictinary() ?? {};
    const initialCols = this.isExpanded ? buildExpandedRunsColumns() : buildCollapsedRunsColumns();
    // Runs exist only for a Test that has local labels, so in Backlog the assignee shown on these rows is
    // another label's roll-up value - see BacklogRollupHelper. Edit it from that label.
    const lockAssignee = !!this.selectedWorkspace?.isBacklog;
    const cols = hydrateColumnFieldIds(initialCols, fieldIdDictionary).map((c) => (lockAssignee && c.fieldName === 'assignee' ? { ...c, isEditable: false } : c));
    const result = this.nodeTableHelper.normalizeFrozenColumns(cols, initialCols);
    this.frozenColumns = result.frozenCols;
    this.scrollableColumns = result.scrollableCols;
    this.tableMinWidth = this.isExpanded ? '50rem' : 'auto';
    this.cdr.markForCheck();
  }

  private buildTreeNodes(rows: List<ITestRunRowModel>): TreeNode[] {
    if (!rows || rows.size === 0) {
      return [];
    }
    // Snapshot expansion state from the existing tree so optimistic store
    // updates (e.g. inline-edit PATCH) don't collapse rows the user toggled
    // open. PrimeNG mutates `expanded` in place on the same TreeNode the
    // toggler clicks, so reading from `this.rows` is the source of truth.
    const expandedById = this.captureExpandedState(this.rows);

    const result: TreeNode[] = [];
    let currentRunNode: TreeNode | null = null;

    for (const r of rows.toArray()) {
      if (!r) continue;
      // Lowercase nodeType so the shared table's ngModel lookup
      // `fieldIdDictionary[nodeType + ' ' + fieldName]` matches the dict's
      // lowercase keys (e.g. 'test status' for the Test row).
      const data = { ...r, id: r.rowId, nodeType: (r.nodeType || '').toLowerCase() };
      const expanded = expandedById.get(r.rowId) ?? false;
      if (r.rowKind === LABEL_RUN_KIND) {
        currentRunNode = { data, children: [], expanded };
        result.push(currentRunNode);
      } else if (r.rowKind === RELATED_BUG_KIND) {
        const bugNode: TreeNode = { data, children: [], expanded };
        if (currentRunNode) {
          currentRunNode.children!.push(bugNode);
        } else {
          result.push(bugNode);
        }
      }
    }
    return result;
  }

  private captureExpandedState(nodes: TreeNode[] | null | undefined): Map<string, boolean> {
    const map = new Map<string, boolean>();
    if (!nodes?.length) return map;
    const walk = (list: TreeNode[]): void => {
      for (const n of list) {
        const id = (n?.data as { id?: string } | undefined)?.id;
        if (id) map.set(id, !!n.expanded);
        if (n?.children?.length) walk(n.children);
      }
    };
    walk(nodes);
    return map;
  }

  public onCellEdit(event: ICellEditEvent<any>): void {
    const row = event?.row as (ITestRunRowModel & { id?: string }) | null;
    if (!row) return;
    const original = this.rowsList.find((r) => r?.rowId === (row.id ?? row.rowId));
    if (!original) return;

    const cellEvent: ITestRunCellEditEvent = {
      row: original,
      fieldId: this.resolveFieldIdForRow(original, event.column),
      fieldName: event.column.fieldName,
      fieldDisplayName: event.column.fieldDisplayName,
      value: event.value,
      previousValue: event.previousValue,
    };
    this.testRunsStore.patchRun(cellEvent).subscribe({
      next: (fresh) => {
        if (!fresh || !cellEvent.row.nodeId) return;
        const stringValue = cellEvent.value == null ? '' : String(cellEvent.value);
        // A test run row's edit is per-label (PatchTestRun writes that label's NodeLocalized), and
        // so is a Summary edit on any row (stored under the synthetic LocalizedNameKey). Only mirror
        // those into WorkspaceStore when the edited row's label is the currently-viewed label.
        // Otherwise another label's value lands on this label's node, the Overview form is rebuilt
        // from it, and the next panel save persists it here (or onto the universal node, for a
        // non-extended field). Bug rows write the bug's global node, so their other fields are
        // correct in every label and are always mirrored.
        const isPerLabelEdit = cellEvent.fieldName === FIELD_NAME_NAME_LOWER || cellEvent.row.rowKind !== RELATED_BUG_KIND;
        if (isPerLabelEdit) {
          const currentLabelId = this.workspaceStore.selectedWorkspaceInstance?.id;
          if (!currentLabelId || cellEvent.row.labelId !== currentLabelId) {
            return;
          }
        }
        this.workspaceStore.syncNodeFieldFromRunEdit(
          cellEvent.row.nodeId,
          cellEvent.fieldName,
          cellEvent.fieldId,
          stringValue,
        );
      },
      // Errors are already surfaced via $lastError on the store, and the
      // optimistic write has been rolled back — swallow here so the stream
      // doesn't bubble an unhandled error.
      error: () => undefined,
    });
  }

  private resolveFieldIdForRow(row: ITestRunRowModel, column: IColumnModel): string {
    // The column's fieldId is hydrated blueprint-wide from the field NAME, but fields
    // like "status" map to different IDs per node type (Test → "Test Status", Bug → "Status").
    // Mirror the ngModel path used in the shared table: fieldIdDictionary[nodeType + ' ' + fieldName] || col.fieldId.
    const dict = this.nodeHelper.getFieldNameToIdDictinary() ?? {};
    const nodeType = (row?.nodeType || '').toLowerCase();
    const fieldName = (column?.fieldName || '').toLowerCase();
    const perNodeTypeKey = nodeType && fieldName ? `${nodeType} ${fieldName}` : '';
    return (perNodeTypeKey && dict[perNodeTypeKey]) || column?.fieldId || '';
  }

  public onSortChange(event: ISortChangeEvent): void {
    this.sortField = event.field;
    this.sortOrder = event.order;
  }

  public isRowEditable = (row: ITestRunRowModel & { id?: string }): boolean => {
    if (!row) return false;
    return row.rowKind === LABEL_RUN_KIND || row.rowKind === RELATED_BUG_KIND;
  };
}
